import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

const empty = {
  reifenart: '', reifenmarke: '', reifengroesse: '',
  profiltiefe: '', zustand: '', einlagerungsort: '',
  montiert: false, notizen: '',
  fremdeingelagert: false, lagerort_betrieb_id: null
};

function getProfilWarnung(reifenart, profiltiefe) {
  const val = parseFloat(String(profiltiefe).replace(',', '.'));
  if (!reifenart || !profiltiefe || isNaN(val)) return null;
  if (reifenart === 'Sommerreifen' && val < 3)
    return `Sommerreifen: Profiltiefe unter 3 mm (${val} mm) — Reifenwechsel empfohlen!`;
  if ((reifenart === 'Winterreifen' || reifenart === 'Ganzjahresreifen') && val < 5)
    return `${reifenart}: Profiltiefe unter 5 mm (${val} mm) — Reifenwechsel empfohlen!`;
  return null;
}

function LagerortPicker({ value, onChange }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selected, setSelected] = useState(null);
  const timer = useRef(null);

  // Load selected betrieb name on mount if value is set
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    api.getBetriebe().then(all => {
      const found = all.find(b => b.id === value);
      if (found) setSelected(found);
    }).catch(() => {});
  }, []); // eslint-disable-line

  const search = (q) => {
    clearTimeout(timer.current);
    setQuery(q);
    if (!q.trim()) { setSuggestions([]); setShowSuggestions(false); return; }
    timer.current = setTimeout(() => {
      api.searchBetriebe(q).then(results => {
        setSuggestions(results);
        setShowSuggestions(true);
      }).catch(() => {});
    }, 300);
  };

  const select = (b) => {
    setSelected(b);
    onChange(b.id);
    setQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const clear = () => {
    setSelected(null);
    onChange(null);
  };

  if (selected) {
    return (
      <div style={{ padding: '10px 12px', background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{selected.name}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>{selected.typ}</div>
            {selected.telefon && (
              <a href={`tel:${selected.telefon}`} style={{ display: 'block', fontSize: 12, color: 'var(--primary)', fontWeight: 500 }}>📞 {selected.telefon}</a>
            )}
            {selected.email && (
              <a href={`mailto:${selected.email}`} style={{ display: 'block', fontSize: 12, color: 'var(--primary)' }}>✉️ {selected.email}</a>
            )}
          </div>
          <button type="button" onClick={clear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: 18, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="form-input"
        placeholder="Betrieb suchen (Werkstatt, Reifenservice…)"
        value={query}
        onChange={e => search(e.target.value)}
        onFocus={() => query && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
      />
      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,.1)', maxHeight: 200, overflowY: 'auto'
        }}>
          {suggestions.map(b => (
            <div key={b.id} onMouseDown={() => select(b)} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{b.name}</span>
              <span style={{ fontSize: 11, color: 'var(--gray-500)', marginLeft: 8 }}>{b.typ}</span>
            </div>
          ))}
        </div>
      )}
      {showSuggestions && suggestions.length === 0 && query.trim() && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: 'var(--gray-500)' }}>
          Kein Betrieb gefunden. Im Kontaktbuch anlegen.
        </div>
      )}
    </div>
  );
}

export default function RadsatzForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial
    ? { ...initial, montiert: !!initial.montiert, fremdeingelagert: !!initial.fremdeingelagert, lagerort_betrieb_id: initial.lagerort_betrieb_id || null }
    : { ...empty }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const profilWarnung = getProfilWarnung(form.reifenart, form.profiltiefe);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(form); }
    catch (err) { setError(err.message || 'Fehler beim Speichern.'); setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{initial ? 'Radsatz bearbeiten' : 'Neuer Radsatz'}</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {profilWarnung && (
            <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
              ⚠️ {profilWarnung}
            </div>
          )}
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Reifenart</label>
                <select className="form-select" value={form.reifenart} onChange={e => set('reifenart', e.target.value)} autoFocus>
                  <option value="">– Bitte wählen –</option>
                  <option>Sommerreifen</option>
                  <option>Winterreifen</option>
                  <option>Ganzjahresreifen</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Reifenmarke</label>
                <input className="form-input" value={form.reifenmarke} onChange={e => set('reifenmarke', e.target.value)} placeholder="z.B. Michelin, Continental" />
              </div>
              <div className="form-group">
                <label className="form-label">Reifengröße</label>
                <input className="form-input" value={form.reifengroesse} onChange={e => set('reifengroesse', e.target.value)} placeholder="z.B. 205/55 R16" />
              </div>
              <div className="form-group">
                <label className="form-label">Profiltiefe (mm)</label>
                <input
                  className="form-input"
                  value={form.profiltiefe}
                  onChange={e => set('profiltiefe', e.target.value)}
                  placeholder="z.B. 6.5"
                  style={profilWarnung ? { borderColor: '#f59e0b', background: '#fffbeb' } : {}}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Zustand</label>
                <select className="form-select" value={form.zustand} onChange={e => set('zustand', e.target.value)}>
                  <option value="">– Bitte wählen –</option>
                  <option>Sehr gut</option><option>Gut</option><option>Mittel</option>
                  <option>Schlecht – bald wechseln</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Einlagerungsort (Freitext)</label>
                <input className="form-input" value={form.einlagerungsort} onChange={e => set('einlagerungsort', e.target.value)} placeholder="z.B. Keller, Garage" />
              </div>
              <div className="form-group form-full">
                <label className="form-label">Notizen</label>
                <input className="form-input" value={form.notizen} onChange={e => set('notizen', e.target.value)} />
              </div>

              {/* Montiert */}
              <div className="form-group form-full">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 8 }}>
                  <input type="checkbox" checked={form.montiert} onChange={e => set('montiert', e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontWeight: 500, color: 'var(--gray-700)' }}>Aktuell am Fahrzeug montiert</span>
                  {form.montiert && <span className="badge badge-success">Montiert</span>}
                </label>
                {form.montiert && (
                  <div style={{ fontSize: 12, color: 'var(--gray-500)', marginLeft: 26 }}>
                    Alle anderen Radsätze dieses Fahrzeugs werden automatisch als „nicht montiert" markiert.
                  </div>
                )}
              </div>

              {/* Fremdeingelagert */}
              <div className="form-group form-full">
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: form.fremdeingelagert ? 10 : 0 }}>
                  <input type="checkbox" checked={form.fremdeingelagert} onChange={e => { set('fremdeingelagert', e.target.checked); if (!e.target.checked) set('lagerort_betrieb_id', null); }} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontWeight: 500, color: 'var(--gray-700)' }}>Fremdeingelagert (bei externem Lagerort)</span>
                  {form.fremdeingelagert && <span className="badge badge-warning">Fremdlager</span>}
                </label>
                {form.fremdeingelagert && (
                  <div style={{ marginLeft: 26 }}>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8 }}>
                      Lagerort aus Kontaktbuch wählen:
                    </div>
                    <LagerortPicker
                      value={form.lagerort_betrieb_id}
                      onChange={id => set('lagerort_betrieb_id', id)}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Abbrechen</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Speichern…' : 'Speichern'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
